import {
  HMDocument,
  HMMetadata,
  HMResourceFetchResult,
  HMResourceVisibility,
  UnpackedHypermediaId,
} from '@seed-hypermedia/client/hm-types'
import {getMetadataName, NavRoute, SearchResult, useValidatedWebRouteLink} from '@shm/shared'
import {useIsHomeDraftOverride} from '@shm/shared/home-draft-context'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {Button} from './button'
import {ScrollArea} from './components/scroll-area'
import {DocumentMaintenanceTrigger} from './document-maintenance'
import {DraftBadge} from './draft-badge'
import {ChevronDown, Close, Menu} from './icons'
import {SmallListItem} from './list-item'
import {useResponsiveItems} from './use-responsive-items'

import {IS_DESKTOP} from '@shm/shared/constants'
import {useIsomorphicLayoutEffect} from '@shm/shared/utils/use-isomorphic-layout-effect'
import {Lock} from 'lucide-react'
import {AssistantPanelHeaderButton, AssistantPanelMenuItem} from './assistant-panel-toggle'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from './components/dropdown-menu'
import {DocNavigationItem, DocumentOutline, DocumentSmallListItem, useNodesOutline} from './navigation'
import {MobileSearch} from './search'
import {Separator} from './separator'
import {SiteLogo} from './site-logo'
import {Tooltip} from './tooltip'
import useMedia from './use-media'
import {cn} from './utils'

// Stable width estimator functions
const getNavItemWidth = () => 150
const HOME_NAVIGATION_KEY = '__site_home__'

/** Returns site navigation with the fixed Home destination first. */
export function getSiteHeaderItems(
  siteHomeId: UnpackedHypermediaId,
  items: DocNavigationItem[] = [],
): DocNavigationItem[] {
  return [
    {
      key: HOME_NAVIGATION_KEY,
      id: {...siteHomeId, path: [], version: null, latest: true},
      metadata: {name: 'Home'},
      isPublished: true,
    },
    ...items,
  ]
}

/** Returns whether a navigation item contains the current document route. */
export function isSiteHeaderItemActive(item: DocNavigationItem, docId: UnpackedHypermediaId | null): boolean {
  if (!item.id || !docId || item.id.uid !== docId.uid) return false
  const itemPath = item.id.path ?? []
  const docPath = docId.path ?? []
  if (item.key === HOME_NAVIGATION_KEY) return docPath.length === 0
  return itemPath.length <= docPath.length && itemPath.every((segment, index) => docPath[index] === segment)
}

function getActiveSiteHeaderItemKey(items: DocNavigationItem[], docId: UnpackedHypermediaId | null) {
  return items
    .filter((item) => isSiteHeaderItemActive(item, docId))
    .sort((a, b) => (b.id?.path?.length ?? 0) - (a.id?.path?.length ?? 0))[0]?.key
}

export function SiteHeader({
  siteHomeId,
  docId,
  items,
  directoryItems: _directoryItems,
  isCenterLayout = false,
  document,
  draftMetadata,
  siteHomeDocument,
  embeds,
  onBlockFocus,
  onShowMobileMenu,
  hideSiteBarClassName,
  isLatest: _isLatest,
  editNavPane,
  editNavPanePortalRef,
  wrapperClassName,
  notifyServiceHost: _notifyServiceHost,
  routeType,
  rightActions,
}: {
  siteHomeId: UnpackedHypermediaId
  docId: UnpackedHypermediaId | null
  items?: DocNavigationItem[] | null
  homeNavigationItems?: DocNavigationItem[]
  directoryItems?: DocNavigationItem[]
  isCenterLayout?: boolean
  document?: HMDocument | undefined
  draftMetadata?: HMMetadata
  siteHomeDocument?: HMDocument | null
  embeds?: HMResourceFetchResult[]
  onBlockFocus?: (blockId: string) => void
  onShowMobileMenu?: (isOpen: boolean) => void
  hideSiteBarClassName?: AutoHideSiteHeaderClassName
  isLatest?: boolean
  editNavPane?: React.ReactNode
  editNavPanePortalRef?: (node: HTMLDivElement | null) => void
  wrapperClassName?: string
  notifyServiceHost?: string
  routeType?: NavRoute['key']
  rightActions?: React.ReactNode
}) {
  const [isMobileMenuOpen, _setIsMobileMenuOpen] = useState(false)
  const [isMobileSearchActive, setIsMobileSearchActive] = useState(false)

  function setIsMobileMenuOpen(isOpen: boolean) {
    _setIsMobileMenuOpen(isOpen)
    onShowMobileMenu?.(isOpen)
    if (!isOpen) {
      setIsMobileSearchActive(false)
    }
  }
  // Determine the home document for logo/branding
  // Priority: current doc if on home page, otherwise siteHomeDocument.
  const homeDraftOverride = useIsHomeDraftOverride()
  const isHomeView = homeDraftOverride ?? !!(docId && !docId.path?.length)
  const homeDoc = isHomeView
    ? {document, id: docId} // On home page — document IS the home doc
    : {document: siteHomeDocument ?? undefined, id: siteHomeId} // Non-home: use site home (may be undefined while loading)
  const headerSearch = (
    <>
      <Button
        variant="ghost"
        size="icon"
        className={cn('md:hidden')}
        onClick={() => {
          setIsMobileMenuOpen(true)
        }}
      >
        <Menu size={20} />
      </Button>
      {siteHomeId && !IS_DESKTOP ? (
        <div className="hidden items-center gap-4 md:flex">
          <div className="flex items-center gap-1">
            <AssistantPanelHeaderButton />
            <DocumentMaintenanceTrigger compact />
          </div>
        </div>
      ) : null}
    </>
  )

  const headerRef = useRef<HTMLDivElement>(null)

  useIsomorphicLayoutEffect(() => {
    const updateHeaderHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 60

      window.document.documentElement.style.setProperty('--site-header-h', `${headerHeight}px`)
      window.document.documentElement.style.setProperty('--site-header-live-h', `${headerHeight}px`)
    }

    // Initial measurement
    updateHeaderHeight()

    // Update on resize
    const resizeObserver = new ResizeObserver(updateHeaderHeight)
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current)
    }

    // Cleanup
    return () => {
      resizeObserver.disconnect()
      window.document.documentElement.style.setProperty('--site-header-h', '0px')
      window.document.documentElement.style.removeProperty('--site-header-live-h')
    }
  }, [headerRef.current])

  if (!homeDoc) return null
  const headerHomeId = homeDoc.id
  if (!headerHomeId) return null
  const navigationItems = getSiteHeaderItems(siteHomeId, items ?? [])
  return (
    <header
      ref={headerRef}
      className={cn(
        'border-border dark:bg-background relative z-40 flex w-full transform-gpu border-b bg-white py-2 pr-4 pl-2 transition-transform duration-200',
        {
          'flex-col': isCenterLayout,
          'flex-row items-center': !isCenterLayout,
        },
        hideSiteBarClassName,
        'sm:translate-y-0',
        wrapperClassName,
      )}
    >
      <div
        className={cn('flex min-w-0 items-center self-stretch sm:shrink-0', {
          'justify-center md:relative': isCenterLayout,
          'flex-start gap-2': !isCenterLayout,
        })}
      >
        <div className={cn('flex min-w-0 overflow-hidden', isCenterLayout ? 'flex-1 justify-center' : '')}>
          <SiteLogo id={headerHomeId} metadata={draftMetadata || homeDoc.document?.metadata} />
        </div>
        {routeType != 'draft' && isCenterLayout ? (
          <div className="flex items-center gap-2 md:absolute md:right-0">
            {!IS_DESKTOP && headerSearch}
            {rightActions}
          </div>
        ) : null}
      </div>

      <div
        className={cn('flex-1 overflow-hidden px-2', {
          flex: !isCenterLayout,
        })}
      >
        <SiteHeaderMenu
          items={navigationItems}
          docId={docId}
          isCenterLayout={isCenterLayout}
          editNavPane={editNavPane}
          editNavPanePortalRef={editNavPanePortalRef}
          siteHomeId={siteHomeId}
        />
      </div>

      <div className="flex items-center gap-2">
        {!isCenterLayout && headerSearch}
        {!isCenterLayout && rightActions}
      </div>
      <MobileMenu
        open={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        renderContent={() => (
          <div className="flex min-h-full flex-col">
            {!IS_DESKTOP && (
              <MobileSearch
                siteHomeId={siteHomeId}
                onSearchActiveChange={setIsMobileSearchActive}
                // @ts-expect-error
                onSelect={(item: SearchResult) => {
                  setIsMobileMenuOpen(false)
                  // Navigation not yet implemented for mobile search results
                }}
              />
            )}

            <div className="relative min-h-0 flex-1">
              {isMobileSearchActive ? <div className="bg-background absolute inset-0 z-10" /> : null}

              <div className="relative z-0">
                {/* Always show the same navigation items as the desktop header */}
                {navigationItems.length > 0 && (
                  <div className="mt-2.5 mb-4 flex flex-col gap-2 px-1">
                    <NavItems
                      items={navigationItems}
                      onClick={() => {
                        setIsMobileMenuOpen(false)
                      }}
                    />
                  </div>
                )}
                {!IS_DESKTOP && (
                  <div className="mb-4 flex flex-col gap-2 px-1">
                    <AssistantPanelMenuItem onClick={() => setIsMobileMenuOpen(false)} />
                    <DocumentMaintenanceTrigger onClick={() => setIsMobileMenuOpen(false)} />
                  </div>
                )}
                {/* 
                  Show directory items when not on home
                  {directoryItems && directoryItems.length > 0 && (
                    <>
                      <Separator />
                      <div className="mb-4">
                        <NavItems
                          items={directoryItems}
                          onClick={() => {
                            setIsMobileMenuOpen(false)
                          }}
                        />
                      </div>
                    </>
                  )} */}

                {/* Show document outline when available */}
                {docId && document && (
                  <>
                    <Separator />
                    <div className="mt-2.5 mb-4 px-1">
                      <MobileMenuOutline
                        onActivateBlock={(blockId) => {
                          setIsMobileMenuOpen(false)
                          onBlockFocus?.(blockId)
                        }}
                        document={document}
                        docId={docId}
                        embeds={embeds}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      />
    </header>
  )
}

function NavItems({items, onClick}: {items?: DocNavigationItem[] | null; onClick?: () => void}) {
  return items
    ? items.map((doc) => {
        if (!doc.id && !doc.draftId && !doc.webUrl) return null
        if (doc.webUrl && !doc.id && !doc.draftId) {
          return <MobileWebLinkItem key={doc.key} item={doc} onClick={onClick} />
        }
        return (
          <DocumentSmallListItem
            onClick={onClick}
            key={doc.key}
            metadata={doc.metadata}
            id={doc.id}
            draftId={doc.draftId}
            isPublished={doc.isPublished}
            visibility={doc.visibility}
          />
        )
      })
    : null
}

function MobileWebLinkItem({item, onClick}: {item: DocNavigationItem; onClick?: () => void}) {
  const {linkProps} = useValidatedWebRouteLink(item.webUrl || null, {onClick})

  return <SmallListItem bold title={getMetadataName(item.metadata)} {...linkProps} />
}

function MobileMenuOutline({
  onActivateBlock,
  document,
  docId,
  embeds,
}: {
  onActivateBlock: (blockId: string) => void
  document: HMDocument
  docId: UnpackedHypermediaId
  embeds?: HMResourceFetchResult[]
}) {
  const outline = useNodesOutline(document, docId, embeds)

  return (
    <DocumentOutline onActivateBlock={onActivateBlock} outline={outline} id={docId} activeBlockId={docId.blockRef} />
  )
}

function OverflowMenuItem({
  item,
}: {
  item: {
    key: string
    id?: UnpackedHypermediaId
    draftId?: string | null
    metadata: HMMetadata
    webUrl?: string
    visibility?: HMResourceVisibility
  }
}) {
  const {linkProps} = useValidatedWebRouteLink(
    item.draftId
      ? {key: 'draft', id: item.draftId}
      : item.id
        ? {key: 'document', id: {...item.id, latest: true, version: null}}
        : item.webUrl || '',
  )
  return (
    <DropdownMenuItem {...linkProps}>
      <div className="flex w-full items-center justify-between gap-2">
        <span>{getMetadataName(item.metadata)}</span>
        {item.visibility === 'PRIVATE' ? <Lock size={12} className="text-muted-foreground" /> : null}
      </div>
    </DropdownMenuItem>
  )
}

function HeaderLinkItem({
  id,
  metadata,
  active,
  draftId,
  webUrl,
  visibility,
}: {
  id?: UnpackedHypermediaId
  draftId?: string | null
  metadata: HMMetadata
  active: boolean
  webUrl?: string | undefined
  visibility?: HMResourceVisibility
}) {
  const {linkProps} = useValidatedWebRouteLink(
    draftId
      ? {
          key: 'draft',
          id: draftId,
          // panel: {key: 'options'},
        }
      : id
        ? {
            key: 'document',
            id: {...id, latest: true, version: null},
          }
        : webUrl || null,
  )
  return (
    <div className={cn('flex items-center gap-1 px-1')}>
      <a
        className={cn(
          'cursor-pointer truncate border-y-2 border-t-transparent px-1 py-1 font-bold transition-colors select-none',
          active
            ? 'border-b-foreground text-foreground'
            : 'text-muted-foreground hover:border-b-accent border-b-transparent',
        )}
        {...linkProps}
      >
        {getMetadataName(metadata)}
      </a>
      {visibility === 'PRIVATE' ? <Lock size={12} className="text-muted-foreground" /> : null}
      {draftId ? <DraftBadge /> : null}
    </div>
  )
}

export function SiteHeaderMenu({
  items,
  docId,
  siteHomeId: _siteHomeId,
  isCenterLayout = false,
  editNavPane,
  editNavPanePortalRef,
}: {
  items?: DocNavigationItem[] | null
  docId: UnpackedHypermediaId | null
  siteHomeId: UnpackedHypermediaId
  isCenterLayout?: boolean
  editNavPane?: React.ReactNode
  editNavPanePortalRef?: (node: HTMLDivElement | null) => void
}) {
  const editNavPaneRef = useRef<HTMLDivElement | null>(null)

  // Calculate reserved width for the edit pane. The responsive hook reserves the
  // overflow trigger only when it is needed.
  const editNavPaneWidth = editNavPaneRef.current?.getBoundingClientRect().width || 0
  const reservedWidth = editNavPaneWidth + 48

  const activeKey = useMemo(() => getActiveSiteHeaderItemKey(items ?? [], docId), [docId, items])

  const {containerRef, itemRefs, visibleItems, overflowItems} = useResponsiveItems({
    items: items || [],
    activeKey,
    getItemWidth: getNavItemWidth,
    reservedWidth,
    gapWidth: 20,
  })

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative hidden w-full flex-1 items-center gap-5 overflow-hidden p-0',
        'md:flex md:py-1 md:pr-0 md:pl-2',
        isCenterLayout ? 'justify-center' : 'justify-start',
      )}
    >
      {/* Hidden measurement container */}
      <div className="pointer-events-none absolute top-0 left-0 flex items-center gap-5 p-0 opacity-0 md:flex md:p-2">
        {items?.map((item) => {
          return (
            <div
              key={`measure-${item.key}`}
              ref={(el) => {
                if (el) {
                  itemRefs.current.set(item.key, el)
                } else {
                  itemRefs.current.delete(item.key)
                }
              }}
            >
              <HeaderLinkItem
                id={item.id}
                metadata={item.metadata}
                draftId={item.draftId}
                active={item.key === activeKey}
                visibility={item.visibility}
              />
            </div>
          )
        })}
        {/* {feedLinkButton} */}
      </div>

      {/* Visible items */}
      {visibleItems.map((item) => {
        return (
          <HeaderLinkItem
            key={item.key}
            id={item.id}
            metadata={item.metadata}
            draftId={item.draftId}
            webUrl={item.webUrl}
            active={item.key === activeKey}
            visibility={item.visibility}
          />
        )
      })}

      {/* Overflow dropdown */}
      {overflowItems.length > 0 && (
        <Tooltip content="More Menu items">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" className="rounded-full">
                <ChevronDown className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-h-[300px] w-50 overflow-y-scroll" side="bottom" align="end">
              {overflowItems.map((item) => (
                <OverflowMenuItem key={item.key} item={item} />
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      )}
      {(editNavPane || editNavPanePortalRef) && (
        <div
          className="shrink-0"
          ref={(node) => {
            editNavPaneRef.current = node
            editNavPanePortalRef?.(node)
          }}
        >
          {editNavPane}
        </div>
      )}
    </div>
  )
}

/** Full-screen mobile navigation, portaled above the header and document controls. */
export function MobileMenu({
  renderContent,
  open,
  onClose,
}: {
  renderContent: () => React.JSX.Element
  open: boolean
  onClose: () => void
}) {
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
  useEffect(() => setPortalTarget(document.body), [])
  if (!portalTarget) return null

  return createPortal(
    <div
      className={cn(
        'bg-background fixed inset-0 z-50 flex h-dvh max-h-dvh flex-col transition-transform duration-200 md:hidden',
        open ? 'translate-x-0' : 'translate-x-full',
      )}
    >
      <div className="flex flex-0 items-center justify-end p-4">
        <Button variant="ghost" size="icon" aria-label="Close navigation menu" onClick={onClose}>
          <Close className="size-4" />
        </Button>
      </div>
      <ScrollArea className="mobile-menu flex-1">
        {open ? renderContent() : null}
        <div className="h-20"></div>
      </ScrollArea>
    </div>,
    portalTarget,
  )
}

export type AutoHideSiteHeaderClassName = 'translate-y-0' | '-translate-y-full'

export function useAutoHideSiteHeader(scrollContainerRef?: React.RefObject<HTMLElement>) {
  const media = useMedia()
  const prevScrollPos = useRef(0)
  const [isHidden, setIsHidden] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      let currentScrollPos: number

      // Get scroll position from appropriate source
      if (media.gtSm && scrollContainerRef?.current) {
        // Desktop: use custom scroll container
        currentScrollPos = scrollContainerRef.current.scrollTop
      } else if (!media.gtSm) {
        // Mobile: use window scroll
        currentScrollPos = window.scrollY
      } else {
        // Desktop without container ref - skip
        return
      }

      const threshold = 10 // Prevent flickering on small movements

      // Only update if scroll difference is significant
      if (Math.abs(currentScrollPos - prevScrollPos.current) < threshold) {
        return
      }

      // Hide when scrolling down past 50px, show when scrolling up
      if (currentScrollPos > prevScrollPos.current && currentScrollPos > 50) {
        setIsHidden(true)
      } else {
        setIsHidden(false)
      }

      prevScrollPos.current = currentScrollPos
    }

    // Attach scroll listener to appropriate target
    if (media.gtSm && scrollContainerRef?.current) {
      // Desktop: listen to custom container
      const container = scrollContainerRef.current
      container.addEventListener('scroll', handleScroll, {passive: true})
      return () => {
        container.removeEventListener('scroll', handleScroll)
      }
    } else if (!media.gtSm) {
      // Mobile: listen to window
      window.addEventListener('scroll', handleScroll, {passive: true})
      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }
    // Desktop without scroll ref - no cleanup needed
    return undefined
  }, [media.gtSm, scrollContainerRef])

  return {
    hideSiteHeaderClassName: isHidden ? '-translate-y-full' : ('translate-y-0' as AutoHideSiteHeaderClassName),
    hideMobileBarClassName: isHidden ? 'opacity-40' : '',
    onScroll: () => {}, // Keep for backward compatibility
  }
}
